import { customTemplateDisplay } from "@/utils/templateNames";
import { useState, useEffect, useCallback } from "react";
import { dismissModal } from "@/utils/modalClose";
import { useNavigate, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  IonCard, IonCardHeader, IonCardTitle, IonList, IonItem, IonItemGroup, IonItemDivider,
  IonLabel, IonAvatar, IonButton, IonIcon, IonSpinner,
} from "@ionic/react";
import { addOutline, playOutline, pauseOutline, trashOutline, repeatOutline, eyeOutline, flashOutline, closeOutline, createOutline } from "ionicons/icons";
import { IonSelect, IonSelectOption, IonInput, IonItem as IonItemRow, IonToggle, IonSpinner as IonSpin } from "@ionic/react";
import { processRecurringRuns, previewRecurringStub, duePeriodsFrom } from "@/utils/recurringStubs";
import EditSheet from "@/components/EditSheet";
import { IonDateInput } from "@/components/DateInput";
import { FLOAT } from "@/components/SettingsRows";
import { confirmAlert } from "@/utils/confirmAlert";
import SwipeRow from "@/components/SwipeRow";
import RowActions from "@/components/RowActions";
import { deleteWithUndo, withoutPendingDeletes } from "@/utils/undoDelete";
import AppLayout from "@/components/AppLayout";
import AppSeo from "@/components/AppSeo";
import EmployeeModal from "@/components/EmployeeModal";
import { invalidateBusinessEmployees } from "@/components/SavedEmployees";
import { uiAvatar } from "@/components/SidebarProfileCard";
import { isUserLoggedIn, userHeaders } from "@/utils/userAuth";
import { useAppLive } from "@/utils/appLive";
import { toast } from "@/utils/toast";
import { toastText } from "@/utils/toastTemplates";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const STUB_TEMPLATES = [["template-a", "Gusto"], ["template-c", "Workday"], ["template-h", "OnPay"], ["template-s", "Sage"]];
// the designs a schedule can run on, by the employee's country (the server checks the same)
const TEMPLATES_BY_COUNTRY = {
  us: [["template-a", "Gusto"], ["template-c", "Workday"], ["template-h", "OnPay"]],
  ca: [["template-a", "Gusto"], ["template-c", "Workday"], ["template-h", "OnPay"]],
  uk: [["template-h", "OnPay"], ["template-s", "Sage"]],
};
const countryOf = (e) => (e && (e.country === "ca" || e.country === "uk") ? e.country : "us");
const COUNTRY_WORD = { us: "US", ca: "Canada", uk: "UK" };
const FREQ_WORD = { weekly: "Weekly", biweekly: "Bi-weekly", fourweekly: "Four-weekly", monthly: "Monthly" };
const DEFAULT_HOURS = { us: { weekly: 40, biweekly: 80 }, ca: { weekly: 40, biweekly: 80 }, uk: { weekly: 37.5, biweekly: 75, fourweekly: 150, monthly: 162.5 } };
const defaultHours = (e) => (DEFAULT_HOURS[countryOf(e)] || DEFAULT_HOURS.us)[e?.payFrequency] || 80;
// legacy "template-b" (ADP, retired) renders as Gusto now
const templateName = (id) => (STUB_TEMPLATES.find(([v]) => v === id) || [null, id === "template-b" ? "Gusto" : id])[1];
// a schedule on a business's own design shows that design's name (resolved by the server)
const stubTemplateLabel = (sc) => customTemplateDisplay(sc, { yours: true })?.label || templateName(sc.template);
const PAUSE_WORDS = { downloads: "out of downloads", subscription: "plan not active", manual: "paused by you", ended: "finished" };

// /app/employees: the business's own people, in the admin Employees page's
// design — one card, a divider naming the group with its count, avatar rows,
// and the floating ADD button opening the employee sheet. Employees belong to
// the business being operated as: switching businesses switches the list.
// The page's segment row in the top bar (the Tax Layouts / Signatures
// pattern): the employees, and their recurring pay stubs.
// ?segment=recurring opens the second.
const SEGMENTS = [
  { value: "employees", label: "Employees", to: "/app/employees" },
  { value: "recurring", label: "Recurring Pay Stubs", to: "/app/employees?segment=recurring" },
];

export default function AppEmployees() {
  const location = useLocation();
  const segment = new URLSearchParams(location.search).get("segment") === "recurring" ? "recurring" : "employees";
  const navigate = useNavigate();
  const signedIn = isUserLoggedIn();
  const [data, setData] = useState(null); // { hasAccess, employees, businessId, businessName }
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768); // phone rows swipe to delete
  const [modal, setModal] = useState(null); // null | { employee: null | {...} }
  const [schedules, setSchedules] = useState([]);
  const [recurSheet, setRecurSheet] = useState(false);
  const [recurForm, setRecurForm] = useState({ employeeId: "", template: "template-a", hoursPerPeriod: "", firstPeriodStart: "", emailToEmployee: false, maxRuns: "" });
  const [recurEdit, setRecurEdit] = useState(null); // the schedule being edited, or null for a new one
  const [recurBusy, setRecurBusy] = useState(false);
  const [recurBiz, setRecurBiz] = useState(null);   // the business's fields, for previews
  const [preview, setPreview] = useState(null);     // { name, img: null|dataUrl }
  const [runNowBusy, setRunNowBusy] = useState(""); // schedule id in flight

  const loadSchedules = useCallback(async () => {
    if (!isUserLoggedIn()) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/recurring-paystubs`, { headers: userHeaders() });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.success !== false) { setSchedules(d.schedules || []); setRecurBiz(d.business || null); }
    } catch { /* offline */ }
  }, []);

  const load = useCallback(async (silent = false) => {
    if (!isUserLoggedIn()) { setData({ hasAccess: false, employees: [] }); return; }
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/employees`, { headers: userHeaders() });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not load employees");
      setData({ ...d, employees: withoutPendingDeletes(d.employees || []) }); // a swipe still deleting stays gone
    } catch (err) {
      if (!silent) toast.error(err.message);
      setData((p) => p || { hasAccess: false, employees: [] });
    }
  }, []);

  useEffect(() => { load(); loadSchedules(); }, [load, loadSchedules]);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // mobile: a full left-swipe removes an employee the way mail apps do: the
  // row is gone at once, a toast offers Undo for a few seconds, then the
  // server delete runs (Undo puts the row back where it was)
  const swipeRemove = (e) => {
    const at = (data?.employees || []).findIndex((x) => x.id === e.id);
    deleteWithUndo({
      noun: "employee",
      ids: [e.id],
      remove: () => setData((p) => (p ? { ...p, employees: (p.employees || []).filter((x) => x.id !== e.id) } : p)),
      restore: () => setData((p) => {
        if (!p) return p;
        const list = [...(p.employees || [])];
        if (list.some((x) => x.id === e.id)) return p;
        list.splice(Math.min(Math.max(at, 0), list.length), 0, e);
        return { ...p, employees: list };
      }),
      // recorded until the server answers, so a refresh still carries it out
      request: { url: `${BACKEND_URL}/api/user/employees/${e.id}`, method: "DELETE", headers: userHeaders() },
      after: () => { invalidateBusinessEmployees?.(); load(true); },
    });
  };
  useAppLive(() => { load(); loadSchedules(); }); // switching businesses re-scopes both lists

  // No plan carrying the feature: Billing is where the plans are
  useEffect(() => {
    if (data && !data.hasAccess) navigate("/app/billing", { replace: true });
  }, [data, navigate]);

  const employees = data?.employees || [];
  const line = (e) => [COUNTRY_WORD[countryOf(e)], e.employeeId, [e.city, e.state || e.province || e.postcode].filter(Boolean).join(", ")].filter(Boolean).join(" · ");

  // ── recurring schedule actions ──
  const recurEmployee = employees.find((e) => e.id === (recurEdit ? recurEdit.employeeId : recurForm.employeeId));
  const recurTemplates = TEMPLATES_BY_COUNTRY[countryOf(recurEmployee)];
  const openRecurSheet = () => {
    const first = employees.find((e) => !schedules.some((sc) => sc.employeeId === e.id));
    setRecurEdit(null);
    setRecurForm({ employeeId: first?.id || "", template: TEMPLATES_BY_COUNTRY[countryOf(first)][0][0], hoursPerPeriod: "", firstPeriodStart: "", emailToEmployee: false, maxRuns: "" });
    setRecurSheet(true);
  };
  // the same sheet edits a schedule: its template, hours, email and stop-after count
  const openRecurEdit = (sc) => {
    setRecurEdit(sc);
    setRecurForm({ employeeId: sc.employeeId, template: sc.template, hoursPerPeriod: String(sc.hoursPerPeriod ?? ""), firstPeriodStart: sc.nextPeriodStart, emailToEmployee: !!sc.emailToEmployee, maxRuns: sc.maxRuns ? String(sc.maxRuns) : "" });
    setRecurSheet(true);
  };
  // a new employee pick keeps the template only if that employee's country offers it
  const pickRecurEmployee = (id) => setRecurForm((f) => {
    const emp = employees.find((e) => e.id === id);
    const allowed = TEMPLATES_BY_COUNTRY[countryOf(emp)];
    return { ...f, employeeId: id ?? "", template: allowed.some(([v]) => v === f.template) ? f.template : allowed[0][0] };
  });

  // a watermarked look at the next stub; no download drawn
  const openPreview = async (sc) => {
    const emp = employees.find((e) => e.id === sc.employeeId);
    if (!emp || !recurBiz) { toast.error("Could not build the preview"); return; }
    setPreview({ name: sc.employeeName, img: null });
    try {
      const img = await previewRecurringStub(sc, { ...emp, ssnLast4: emp.ssnLast4, bankLast4: emp.bankLast4 }, recurBiz);
      setPreview((p) => (p ? { ...p, img } : p));
    } catch (e) {
      console.error(e);
      setPreview(null);
      toast.error("Preview failed");
    }
  };

  const runNow = async (sc) => {
    if (!(await confirmAlert({
      header: `Generate ${sc.employeeName}'s next stub now?`,
      message: "This period's stub is made today and uses one download.",
      confirmText: "Generate",
    }))) return;
    setRunNowBusy(sc.id);
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/recurring-paystubs/${sc.id}/run-now`, { method: "POST", headers: userHeaders() });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not run");
      toast.success(toastText("recurring_run_now", {}, "On it. The stub lands in My Documents in a moment"));
      loadSchedules();
      processRecurringRuns(true);
    } catch (err) { toast.error(err.message); loadSchedules(); } finally { setRunNowBusy(""); }
  };
  const saveRecur = async () => {
    if (recurEdit) {
      setRecurBusy(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/user/recurring-paystubs/${recurEdit.id}`, {
          method: "PUT", headers: userHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({ template: recurForm.template, hoursPerPeriod: recurForm.hoursPerPeriod || null, emailToEmployee: recurForm.emailToEmployee, maxRuns: recurForm.maxRuns || null }),
        });
        const d = await res.json().catch(() => ({}));
        if (!res.ok || d.success === false) throw new Error(d.detail || "Could not save the schedule");
        toast.success("Schedule updated");
        setRecurSheet(false);
        setRecurEdit(null);
        loadSchedules();
      } catch (err) { toast.error(err.message); } finally { setRecurBusy(false); }
      return;
    }
    if (!recurForm.employeeId) { toast.error("Pick an employee"); return; }
    if (!recurForm.firstPeriodStart) { toast.error("Pick the first period's start date"); return; }
    // a start date in the past makes the periods already due right away, each one a download
    const due = recurEmployee ? duePeriodsFrom(recurForm.firstPeriodStart, recurEmployee.payFrequency || "biweekly", recurEmployee.payDay || "Friday") : 0;
    if (due > 1 && !(await confirmAlert({
      header: `Make ${due} stubs now?`,
      message: `That start date puts ${due} pay days in the past${due >= 8 ? " (8 is the most made at once; any more follow on the next run)" : ""}. Each is made as soon as the schedule starts and uses one download. Pick a later date to start from the next pay day instead.`,
      confirmText: "Make them",
    }))) return;
    setRecurBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/recurring-paystubs`, {
        method: "POST", headers: userHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ ...recurForm, maxRuns: recurForm.maxRuns || null }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not create the schedule");
      toast.success(toastText("recurring_on", {}, "Recurring pay stubs on"));
      setRecurSheet(false);
      loadSchedules();
    } catch (err) { toast.error(err.message); } finally { setRecurBusy(false); }
  };
  const toggleRecur = async (sc) => {
    if (sc.status === "active" && !(await confirmAlert({
      header: `Pause recurring stubs for ${sc.employeeName}?`,
      message: "No stubs are made while paused. Resume any time.",
      confirmText: "Pause",
    }))) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/recurring-paystubs/${sc.id}`, {
        method: "PUT", headers: userHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ status: sc.status === "active" ? "paused" : "active" }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not update");
      loadSchedules();
    } catch (err) { toast.error(err.message); }
  };
  const removeRecur = async (sc) => {
    if (!(await confirmAlert({ header: `Stop recurring stubs for ${sc.employeeName}?`, message: "Stubs already made are kept in My Documents.", confirmText: "Stop" }))) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/recurring-paystubs/${sc.id}`, { method: "DELETE", headers: userHeaders() });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not remove");
      toast.success(toastText("recurring_schedule_removed", {}, "Schedule removed"));
      loadSchedules();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <AppLayout
      pageTabs={{
        value: segment,
        items: SEGMENTS,
        // replace, not push: segment hops stay one history entry
        onChange: (v) => { const sg = SEGMENTS.find((x) => x.value === v); if (sg) navigate(sg.to, { replace: true }); },
      }}
    >
      <AppSeo title="Employees" description="Your business's employees, ready for the documents you make." path="/app/employees" />
      {!data || !data.hasAccess ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}><IonSpinner name="crescent" /></div>
      ) : (
        <div className="admin-profile admin-moderators app-employees" style={{ maxWidth: 1200, margin: "0 auto", paddingBottom: 80 }}>
          {/* the Employees tab; with no business yet it is also what the
              Recurring tab shows, since its setup prompt comes first */}
          {(segment === "employees" || !data.businessId) && (
          <IonCard className="mods-card">
            <IonCardHeader>
              <IonCardTitle className="type-title"><span>Employees</span></IonCardTitle>
            </IonCardHeader>
            {!data.businessId ? (
              <div className="branding-note" style={{ padding: "4px 20px 22px", maxWidth: 560 }}>
                <p style={{ margin: "0 0 16px", color: "var(--ion-color-medium)", fontSize: "0.95rem", lineHeight: 1.5 }}>
                  Employees belong to a business. Set yours up first, then add the people who work there.
                </p>
                <IonButton color="success" onClick={() => navigate("/app/business")}>Set up your business</IonButton>
              </div>
            ) : (
              <IonList>
                <IonItemGroup>
                  <IonItemDivider className="border-top">
                    <IonLabel>{data.businessName || "Your business"}</IonLabel>
                    <span slot="end" className="mods-count">{employees.length}</span>
                  </IonItemDivider>
                  {employees.length === 0 ? (
                    <IonItem lines="none"><IonLabel>None yet. Add your first employee.</IonLabel></IonItem>
                  ) : employees.map((e, idx) => {
                    const row = (
                      <IonItem key={e.id} button detail lines={idx === employees.length - 1 ? "none" : undefined}
                        onClick={() => setModal({ employee: e })} className="mod-row">
                        <IonAvatar slot="start">
                          <img alt="" src={uiAvatar(e.name)} />
                        </IonAvatar>
                        <IonLabel>
                          {e.name}
                          <p>{line(e) || "No details yet"}</p>
                        </IonLabel>
                      </IonItem>
                    );
                    // a phone row swipes left to remove the employee
                    return isMobile ? <SwipeRow key={e.id} onDelete={() => swipeRemove(e)}>{row}</SwipeRow> : row;
                  })}
                </IonItemGroup>
              </IonList>
            )}
          </IonCard>
          )}

          {/* ── Recurring pay stubs: each due pay day draws a download and
                 the stub lands in this business's My Documents ── */}
          {segment === "recurring" && data.businessId && (
            <IonCard className="mods-card recurring-card">
              <IonCardHeader>
                <IonCardTitle className="type-title">
                  <span>Recurring Pay Stubs</span>
                  <IonButton size="small" fill="outline" onClick={openRecurSheet} disabled={employees.length === 0}
                    title="Set up automatic pay stubs on every pay day"
                    style={{ "--color": "var(--ion-text-color, #0d1b2a)", "--border-color": "var(--ion-text-color, #0d1b2a)" }}>
                    <IonIcon slot="start" icon={repeatOutline} /> New schedule
                  </IonButton>
                </IonCardTitle>
              </IonCardHeader>
              <IonList>
                <IonItemGroup>
                  <IonItemDivider className="border-top">
                    <IonLabel>Runs on each pay day · one download per stub</IonLabel>
                    <span slot="end" className="mods-count">{schedules.length}</span>
                  </IonItemDivider>
                  {schedules.length === 0 ? (
                    <IonItem lines="none"><IonLabel color="medium">None yet. Pay stubs can make themselves on every pay day.</IonLabel></IonItem>
                  ) : schedules.map((sc, idx) => (
                    <IonItem key={sc.id} button detail={false} onClick={() => openPreview(sc)}
                      lines={idx === schedules.length - 1 ? "none" : undefined} className="mod-row sig-row recur-row">
                      <div className="sig-main">
                        <IonAvatar className="sig-avatar"><img alt="" src={uiAvatar(sc.employeeName)} /></IonAvatar>
                        <div className="sig-text">
                          <div className="sig-name">
                            <span className="sig-who">{sc.employeeName}</span>
                            <span className={`admin-badge ${sc.status === "active" ? "admin-badge-green" : "admin-badge-amber"}`}>
                              {sc.status === "active" ? "Active" : `Paused: ${PAUSE_WORDS[sc.pausedReason] || "paused"}`}
                            </span>
                          </div>
                          <p className="sig-sub">{COUNTRY_WORD[sc.country] || "US"} · {stubTemplateLabel(sc)} · {FREQ_WORD[sc.payFrequency] || "Bi-weekly"} on {sc.payDay}s · next period {sc.nextPeriodStart}{sc.maxRuns ? ` · ${sc.runs || 0} of ${sc.maxRuns} made` : ""}{sc.emailToEmployee ? " · emailed to them" : ""}</p>
                        </div>
                        {/* icons wide, one menu button on the phone: the row stays the height of the employee rows */}
                        <RowActions isMobile={isMobile} actions={[
                          { key: "preview", icon: eyeOutline, label: "Preview next stub", run: () => openPreview(sc) },
                          { key: "edit", icon: createOutline, label: "Edit schedule", run: () => openRecurEdit(sc) },
                          { key: "run", icon: flashOutline, label: "Run now", title: "Run now: make this period's stub today (uses one download)", disabled: sc.status !== "active", busy: runNowBusy === sc.id, run: () => runNow(sc) },
                          { key: "toggle", icon: sc.status === "active" ? pauseOutline : playOutline, label: sc.status === "active" ? "Pause schedule" : "Resume schedule", run: () => toggleRecur(sc) },
                          { key: "remove", icon: trashOutline, label: "Remove schedule", color: "danger", run: () => removeRecur(sc) },
                        ]} />
                      </div>
                    </IonItem>
                  ))}
                </IonItemGroup>
              </IonList>
            </IonCard>
          )}
        </div>
      )}

      {/* Floating Add button, exactly the admin Employees page's */}
      {/* the floating Add is the Employees tab's; Recurring has New schedule */}
      {segment === "employees" && data?.hasAccess && data?.businessId && createPortal(
        <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: 200 }}>
          <IonButton
            className="mods-add"
            onClick={() => setModal({ employee: null })}
            style={{ "--background": "var(--ion-color-primary)", "--background-activated": "var(--ion-color-primary-shade)", "--background-hover": "var(--ion-color-primary-shade)", "--color": "var(--ion-color-primary-contrast)", "--box-shadow": "0 6px 20px rgba(0,0,0,0.25)" }}
          >
            <span slot="start" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0, flexShrink: 0, fontSize: "1rem" }}>
              <IonIcon icon={addOutline} style={{ fontSize: "inherit", color: "inherit", pointerEvents: "none" }} />
            </span>
            ADD
          </IonButton>
        </div>,
        document.querySelector("ion-app") || document.body
      )}

      {recurSheet && (
        <EditSheet title={recurEdit ? `Edit ${recurEdit.employeeName}'s schedule` : "New recurring schedule"} onClose={() => { setRecurSheet(false); setRecurEdit(null); }} onSave={saveRecur} saving={recurBusy} className="recurring-sheet">
          {!recurEdit && (
          <IonSelect {...FLOAT} label="Employee *" interface="popover" value={recurForm.employeeId}
            onIonChange={(e) => pickRecurEmployee(e.detail.value)}>
            {employees.map((e) => (
              <IonSelectOption key={e.id} value={e.id} disabled={schedules.some((sc) => sc.employeeId === e.id)}>
                {e.name}{schedules.some((sc) => sc.employeeId === e.id) ? " (scheduled)" : ""}
              </IonSelectOption>
            ))}
          </IonSelect>
          )}
          <IonSelect {...FLOAT} label="Template *" interface="popover" value={recurForm.template}
            onIonChange={(e) => setRecurForm((f) => ({ ...f, template: e.detail.value ?? recurTemplates[0][0] }))}>
            {recurTemplates.map(([v, l]) => <IonSelectOption key={v} value={v}>{l} Style</IonSelectOption>)}
          </IonSelect>
          {recurEmployee && recurEmployee.payType !== "salary" && (
            <IonInput {...FLOAT} label="Hours per period" type="number" inputmode="decimal"
              placeholder={String(defaultHours(recurEmployee))}
              value={recurForm.hoursPerPeriod}
              onIonInput={(e) => setRecurForm((f) => ({ ...f, hoursPerPeriod: e.detail.value ?? "" }))} />
          )}
          {!recurEdit && (
            <IonDateInput label="First period starts *" value={recurForm.firstPeriodStart}
              onChange={(v) => setRecurForm((f) => ({ ...f, firstPeriodStart: v ?? "" }))} />
          )}
          <IonInput {...FLOAT} label="Stop after this many stubs (Optional)" type="number" inputmode="numeric" placeholder="Never, runs until stopped"
            value={recurForm.maxRuns} onIonInput={(e) => setRecurForm((f) => ({ ...f, maxRuns: (e.detail.value ?? "").replace(/\D/g, "") }))} />
          {recurEmployee && recurEmployee.email && (
            <IonItemRow lines="none" style={{ "--padding-start": "2px" }}>
              <IonToggle checked={recurForm.emailToEmployee} onIonChange={(e) => setRecurForm((f) => ({ ...f, emailToEmployee: e.detail.checked }))}>
                Email each stub to {recurEmployee.email}
              </IonToggle>
            </IonItemRow>
          )}
          {recurEmployee && (
            <p className="admin-profile-sheet-note">
              Runs {(FREQ_WORD[recurEmployee.payFrequency] || "Bi-weekly").toLowerCase()} on {recurEmployee.payDay || "Friday"}s
              (from the employee's pay details, which it follows if they change), on the {COUNTRY_WORD[countryOf(recurEmployee)]} stub. Each stub uses one download and lands in My Documents;
              if the downloads run out the schedule pauses and you are notified here and by email.
            </p>
          )}
        </EditSheet>
      )}

      {/* the next stub, watermarked, in the shared slide-up preview modal */}
      {preview && createPortal(
        <div className="modal-backdrop pm-modal recurring-preview-modal" style={{ position: "fixed", inset: 0, zIndex: 10015, background: window.innerWidth >= 768 ? "rgba(0,0,0,0.55)" : "var(--ion-background-color, #f2f2f7)", display: "flex", alignItems: window.innerWidth >= 768 ? "center" : "stretch", justifyContent: window.innerWidth >= 768 ? "center" : "stretch" }}>
          <div className="modal-slide-up" style={{ background: "var(--ion-background-color)", color: "var(--ion-text-color)", display: "flex", flexDirection: "column", width: "100%", maxWidth: window.innerWidth >= 768 ? 640 : "100%", height: window.innerWidth >= 768 ? "auto" : "100%", maxHeight: window.innerWidth >= 768 ? "92vh" : "100%", overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", padding: "10px 8px", background: "var(--ion-card-background)", flexShrink: 0 }}>
              <IonButton fill="clear" shape="round" onClick={(e) => dismissModal(() => setPreview(null), e)} aria-label="Close">
                <IonIcon slot="icon-only" icon={closeOutline} style={{ fontSize: 26, color: "var(--ion-text-color)" }} />
              </IonButton>
              <span style={{ fontWeight: 700, fontSize: "1.05rem" }}>Next stub: {preview.name}</span>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
              {preview.img
                ? <img src={preview.img} alt={`${preview.name} pay stub preview`} style={{ display: "block", width: "100%", borderRadius: 8 }} />
                : <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 320 }}><IonSpin name="crescent" /><span style={{ marginTop: 8, fontSize: "0.8rem", color: "var(--ion-color-medium)" }}>Building the preview…</span></div>}
              <p style={{ textAlign: "center", fontSize: "0.75rem", color: "var(--ion-color-medium)", marginTop: 8 }}>A look ahead: no download used. The real one has no watermark.</p>
            </div>
          </div>
        </div>,
        document.querySelector("ion-app") || document.body
      )}

      {modal && (
        <EmployeeModal
          employee={modal.employee}
          businessName={data?.businessName || ""}
          onClose={() => setModal(null)}
          onSaved={() => { invalidateBusinessEmployees(); load(true); }}
        />
      )}
    </AppLayout>
  );
}
